import { useState, useCallback } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	TextField,
	Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { isAxiosError } from 'axios';

import { Section, SectionCard, SectionHeader } from '@/components/common';
import { RichTextEditor } from '@/components/common/RichTextEditor/RichTextEditor';
import {
	useNotificationTemplate,
	useUpdateNotificationTemplate,
} from '@/queries/useNotificationTemplates';
import { useAuthStore } from '@/stores/useAuthStore';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/dateFormatter';

function extractPlaceholders(text: string): string[] {
	const regex = /\{\{\s*(\w+)\s*\}\}/g;
	const placeholders: string[] = [];
	let match;
	while ((match = regex.exec(text)) !== null) {
		if (!placeholders.includes(match[1])) {
			placeholders.push(match[1]);
		}
	}
	return placeholders;
}

export function NotificationTemplateEditPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const permissions = useAuthStore((state) => state.permissions);
	const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
	const canAccess = hasAnyRole(['Admin']);
	const templateId = Number(id);

	const { data: template, isLoading, isError } = useNotificationTemplate(templateId);
	const updateMutation = useUpdateNotificationTemplate();
	const showNotification = useNotificationStore((state) => state.showNotification);

	const [editSubject, setEditSubject] = useState<string | null>(null);
	const [editBody, setEditBody] = useState<string | null>(null);
	const [validationError, setValidationError] = useState<string | null>(null);

	const handleBodyChange = useCallback((html: string) => {
		setEditBody(html);
		setValidationError(null);
	}, []);

	const handleInsertPlaceholder = useCallback((placeholderName: string) => {
		setEditSubject((prev) => `${prev ?? template?.subject ?? ''}{{ ${placeholderName} }}`);
	}, [template?.subject]);

	if (permissions && !canAccess) {
		return <Navigate to={paths.unauthorized} replace />;
	}

	// Initialize edit state from template data (once loaded)
	const subject = editSubject ?? template?.subject ?? '';
	const body = editBody ?? template?.body ?? '';

	const handleSubjectChange = (newValue: string) => {
		setEditSubject(newValue);
		setValidationError(null);
	};

	const handleCancel = () => {
		navigate(paths.admin.notificationTemplates);
	};

	const handleSave = async () => {
		if (!template) return;

		const currentSubject = editSubject ?? template.subject;
		const currentBody = editBody ?? template.body;

		// Validate placeholders
		const usedPlaceholders = [
			...extractPlaceholders(currentSubject),
			...extractPlaceholders(currentBody),
		];
		const unknownPlaceholders = usedPlaceholders.filter(
			(p) => !template.availablePlaceholders.includes(p)
		);

		if (unknownPlaceholders.length > 0) {
			setValidationError(
				`Unknown placeholder(s): ${unknownPlaceholders.map((p) => `'${p}'`).join(', ')}. Only pre-defined placeholders are allowed for this template.`
			);
			return;
		}

		setValidationError(null);

		try {
			await updateMutation.mutateAsync({
				id: templateId,
				subject: currentSubject,
				body: currentBody,
				rowVersion: template.rowVersion,
			});
			showNotification('Template saved successfully.', 'success');
		} catch (error) {
			if (isAxiosError(error) && error.response?.status === 409) {
				// Reset local edits so fresh data from cache invalidation is used
				setEditSubject(null);
				setEditBody(null);
				return;
			}
			showNotification('Failed to save template.', 'error');
		}
	};

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (isError || !template) {
		return (
			<Box sx={{ p: 3 }}>
				<Alert severity="error">Failed to load notification template.</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
				<IconButton
					onClick={() => navigate(paths.admin.notificationTemplates)}
					aria-label="Back to notification templates"
				>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant="h4" component="h1">
					{template.name}
				</Typography>
			</Box>

			<Section>
				<SectionHeader>EDIT TEMPLATE</SectionHeader>
				<SectionCard>
					<Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
						<Box>
							<Typography variant="caption" color="text.secondary">
								Trigger
							</Typography>
							<Typography>{template.trigger}</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="text.secondary">
								Recipient
							</Typography>
							<Box sx={{ mt: 0.5 }}>
								<Chip
									label={template.recipient}
									size="small"
									variant="outlined"
									color="secondary"
								/>
							</Box>
						</Box>
						{template.ccRecipient && (
							<Box>
								<Typography variant="caption" color="text.secondary">
									CC Recipient
								</Typography>
								<Box sx={{ mt: 0.5 }}>
									<Chip
										label={template.ccRecipient}
										size="small"
										variant="outlined"
										color="secondary"
									/>
								</Box>
							</Box>
						)}
						<Box>
							<Typography variant="caption" color="text.secondary">
								Last Modified
							</Typography>
							<Typography>
								{formatDate(template.updatedAt)}
								{template.updatedByName ? ` by ${template.updatedByName}` : ''}
							</Typography>
						</Box>
					</Box>
				</SectionCard>
			</Section>

			<Section>
				<SectionHeader>CONTENT</SectionHeader>
				<SectionCard>
					<Alert severity="warning" sx={{ mb: 2 }}>
						Dynamic fields shown as chips are required by the system. You may edit the surrounding text freely.
						You can remove the dynamic fields but this may break the template.
					</Alert>

					{validationError && (
						<Alert severity="error" sx={{ mb: 2 }} role="alert">
							{validationError}
						</Alert>
					)}

					<Box sx={{ mb: 2 }}>
						<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
							Subject
						</Typography>
						<TextField
							fullWidth
							size="small"
							value={subject}
							onChange={(e) => handleSubjectChange(e.target.value)}
							aria-label="Email subject"
						/>
					</Box>

					<Box sx={{ mb: 2 }}>
						<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
							Body
						</Typography>
						<RichTextEditor
							content={body}
							onChange={handleBodyChange}
						/>
					</Box>

					<Box>
						<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
							Available dynamic fields in this template
						</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
							{template.availablePlaceholders.map((placeholder) => (
								<Chip
									key={placeholder}
									label={placeholder}
									size="small"
									onClick={() => handleInsertPlaceholder(placeholder)}
									clickable
									variant="outlined"
									color="secondary"
									aria-label={`Insert placeholder ${placeholder}`}
								/>
							))}
						</Box>
					</Box>
				</SectionCard>
			</Section>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
				<Button
					variant="outlined"
					onClick={handleCancel}
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="success"
					onClick={handleSave}
					disabled={updateMutation.isPending}
				>
					Save
				</Button>
			</Box>
		</Box>
	);
}

export default NotificationTemplateEditPage;
